import type { TimeRange } from '@/stores/time-filter-store'

/**
 * Parse a value that might be an ISO string, epoch number, or `null`/`undefined`
 * into a numeric ms. Returns `null` when the value is missing or unparseable.
 *
 * Backend stamps temporal data as ISO-8601 strings (`_temporal_min` etc.) but
 * track points and timeline events sometimes carry numbers — accept both.
 */
export function parseTime(raw: unknown): number | null {
  if (raw == null) return null
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw
  if (typeof raw === 'string' && raw.length > 0) {
    const t = Date.parse(raw)
    return Number.isNaN(t) ? null : t
  }
  return null
}

/**
 * Read `_temporal_min` / `_temporal_max` from a properties bag (set by the
 * backend on entity save). Returns `null` for nodes that have no temporal
 * stamp — they are time-agnostic and the filter should not hide them.
 */
export function readTemporalExtent(
  properties: Record<string, unknown> | undefined | null
): TimeRange | null {
  if (!properties) return null
  const min = parseTime(properties._temporal_min)
  const max = parseTime(properties._temporal_max)
  if (min == null && max == null) return null
  const start = min ?? max ?? 0
  const end = max ?? min ?? 0
  return { start: Math.min(start, end), end: Math.max(start, end) }
}

/** True if the entity's temporal extent overlaps the filter range. */
export function overlapsRange(extent: TimeRange | null, filter: TimeRange | null): boolean {
  if (filter == null) return true // no filter = show
  if (extent == null) return true // no temporal stamp = always visible (don't hide neutral nodes)
  return extent.end >= filter.start && extent.start <= filter.end
}

/** Upper bound on histogram bins — keeps the SVG render cheap and prevents
 *  callers from accidentally allocating arbitrarily large arrays. */
const MAX_BIN_COUNT = 200

/**
 * Build a density histogram from a list of timestamps. Returns intensity per
 * bin normalized to 0..1. `binCount` is clamped to [1, MAX_BIN_COUNT].
 */
export function buildDensity(
  bounds: TimeRange,
  timestamps: number[],
  binCount = 80
): { count: number }[] {
  const span = bounds.end - bounds.start
  if (span <= 0 || timestamps.length === 0) {
    return []
  }
  const effectiveBinCount = Math.max(1, Math.min(binCount, MAX_BIN_COUNT))
  const bins = new Array(effectiveBinCount).fill(0)
  for (const t of timestamps) {
    if (t < bounds.start || t > bounds.end) continue
    const idx = Math.min(
      effectiveBinCount - 1,
      Math.floor(((t - bounds.start) / span) * effectiveBinCount)
    )
    bins[idx] += 1
  }
  const max = Math.max(...bins, 1)
  return bins.map(c => ({ count: c / max }))
}

/**
 * Smart default — fit bounds to the supplied timestamps with a small amount
 * of natural padding so the user can extend the window from the handles.
 * Padding is 10% of the data span, clamped to a minute floor / a year ceiling.
 */
export function fitBounds(timestamps: number[]): TimeRange | null {
  if (timestamps.length === 0) return null
  let min = Infinity
  let max = -Infinity
  for (const t of timestamps) {
    if (t < min) min = t
    if (t > max) max = t
  }
  if (min === Infinity) return null
  if (min === max) {
    // single point — pad ±12h so the axis is renderable
    return { start: min - 12 * 3_600_000, end: max + 12 * 3_600_000 }
  }
  const dataSpan = max - min
  const padding = Math.max(60_000, Math.min(dataSpan * 0.1, 365 * 86_400_000))
  return { start: min - padding, end: max + padding }
}

/**
 * Outlier-resistant fit. Returns the [P5, P95] window with a small padding,
 * so a few far-flung timestamps (typo dates, scheduled-future events, etc.)
 * don't stretch the axis across decades while the meaningful cluster sits
 * in a few weeks. Falls back to {@link fitBounds} when there's not enough
 * data for percentiles to be meaningful.
 */
export function smartFitBounds(
  timestamps: number[],
  options?: { lowerPct?: number; upperPct?: number; paddingPct?: number }
): TimeRange | null {
  if (timestamps.length === 0) return null
  if (timestamps.length < 8) return fitBounds(timestamps)
  const lower = options?.lowerPct ?? 0.05
  const upper = options?.upperPct ?? 0.95
  const paddingFrac = options?.paddingPct ?? 0.05
  const sorted = [...timestamps].sort((a, b) => a - b)
  const lo = sorted[Math.floor(sorted.length * lower)]
  const hi = sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * upper))]
  if (lo === undefined || hi === undefined || lo === hi) return fitBounds(timestamps)
  const span = hi - lo
  const pad = Math.max(60_000, span * paddingFrac)
  return { start: lo - pad, end: hi + pad }
}
